/// <reference types="vite/client" />
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import * as React from 'react'
import { createServerFn } from '@tanstack/react-start'
import { auth } from '@clerk/tanstack-react-start/server'
import { AppShell, ColorSchemeScript, MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { cssVariablesResolver, defaultTheme } from '../styles/theme/default-theme'
import type { QueryClient } from '@tanstack/react-query'
import type { ConvexQueryClient } from '@convex-dev/react-query'
import type { ConvexReactClient } from 'convex/react'
import { NotificationContainer } from '~/components/notifications/NotificationContainer'
import { DefaultCatchBoundary } from '~/components/common/DefaultCatchBoundary'
import { NotFound } from '~/components/NotFound'
import appCss from '~/styles/app.css?url'
import { seo } from '~/utils/seo'
import { DeferredPostHogProvider, shouldInitializePostHog } from '~/lib/posthog'
import Header from '~/features/layout/components/Header'
import Footer from '~/features/layout/components/Footer'
import { AuthLayoutProvider, HEADER_HEIGHT, useAuthLayout } from '~/features/layout/context'
import { CookiesBanner } from '~/components/common/CookiesBanner'
import { useCookieConsent } from '~/hooks/useCookieConsent'

// Lazy-load devtools to avoid unused import warnings and reduce bundle size
const TanStackRouterDevtools = React.lazy(() =>
  import('@tanstack/react-router-devtools').then((mod) => ({
    default: mod.TanStackRouterDevtools,
  }))
)
const ReactQueryDevtools = React.lazy(() =>
  import('@tanstack/react-query-devtools/production').then((mod) => ({
    default: mod.ReactQueryDevtools,
  }))
)

/**
 * Single server-side source for Clerk auth and Convex JWT.
 * - Clerk: auth() from @clerk/tanstack-react-start/server (uses clerkMiddleware in start.ts).
 * - Convex: getToken({ template: 'convex' }) for SSR; set on convexQueryClient.serverHttpClient.
 * Protected routes (e.g. _authed) use context.userId from this; they must not call auth() again.
 */
const fetchClerkAuth = createServerFn({ method: 'GET' }).handler(async () => {
  const { userId, getToken } = await auth()
  
  // Get Convex token for SSR queries
  let token: string | null = null
  try {
    token = await getToken({ template: 'convex' })
    if (!token && userId) {
      console.warn(
        'Clerk userId exists but Convex token is null. ' +
        'Make sure you have created a JWT template named "convex" in your Clerk dashboard. ' +
        'See: https://docs.convex.dev/auth/clerk'
      )
    }
  } catch (error) {
    // Token fetch can fail if user is not authenticated or template doesn't exist
    if (userId) {
      console.error('Failed to get Convex token:', error)
    }
    token = null
  }

  return {
    userId,
    token,
  }
})

// PostHog is initialized via PostHogProvider below (conditional on environment)

// Service worker registration helper
function registerServiceWorker() {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return

  navigator.serviceWorker
    .register('/sw.js')
    .then((registration) => {
      console.log('Service Worker registered:', registration.scope)
    })
    .catch((error) => {
      console.error('Service Worker registration failed:', error)
    })
}

export const Route = createRootRouteWithContext<{  
  queryClient: QueryClient  
  convexClient: ConvexReactClient  
  convexQueryClient: ConvexQueryClient  
}>()({  
  beforeLoad: async ({ context }) => {  
    const { userId, token } = await fetchClerkAuth()  

    // SSR: Set auth token on Convex HTTP client for authenticated server-side queries
    if (token && context.convexQueryClient.serverHttpClient) {
      context.convexQueryClient.serverHttpClient.setAuth(token)
    }

    return { userId }
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
      ...seo({
        title: 'Anchor | Better home. Lower bills. Less stress.',
        description:
          'Free DIY project guides for home repair, plumbing, electrical, and flooring. Personalized step-by-step plans with tool lists, costs, and expert tips — no signup required.',
        keywords: 'DIY home improvement, home repair guides, how to fix, plumbing DIY, electrical DIY, flooring installation, bathroom tile, home maintenance, personalized project plans',
      }),
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      { rel: 'manifest', href: '/site.webmanifest', color: '#ffffff' },
      { rel: 'icon', href: '/favicon.ico' },
    ],
  }),
  errorComponent: (props) => {
    return (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    )
  },
  notFoundComponent: () => <NotFound />,  
  component: RootComponent,
})

function RootComponent() {
  return (
      <RootDocument>
        <AppShellLayout />
      </RootDocument>
  )
}

function AppShellLayout() {
  return (
    <AuthLayoutProvider>
      <AppShellContent />
    </AuthLayoutProvider>
  )
}

function AppShellContent() {
  const { showHeader, showFooter, footerHeight } = useAuthLayout()
  const { showBanner, setShowBanner } = useCookieConsent()

  return (
    <>
      <AppShell
        layout="default"
        header={{ height: showHeader ? HEADER_HEIGHT : 0 }}
        footer={{ height: showFooter ? footerHeight : 0 }}
      >
        {showHeader && (
          <AppShell.Header withBorder={false}>
            <Header />
          </AppShell.Header>
        )}

        <AppShell.Main
          style={
            showFooter
              ? {
                  paddingBottom:
                    'calc(var(--app-shell-footer-offset, 0rem) + var(--app-shell-padding, 0rem) + env(safe-area-inset-bottom))',
                }
              : undefined
          }
        >
          <Outlet />
        </AppShell.Main>

        {showFooter && (
          <AppShell.Footer>
            <Footer />
          </AppShell.Footer>
        )}

        {showBanner && <CookiesBanner onClose={() => setShowBanner(false)} />}
      </AppShell>
      {/* NotificationContainer must be inside AuthLayoutProvider to access layout context */}
      <NotificationContainer />
    </>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  // Register service worker on client side
  React.useEffect(() => {
    registerServiceWorker()
  }, [])

  // Only initialize PostHog in production (or dev with opt-in)
  const shouldInitPostHog = shouldInitializePostHog(
    import.meta.env.MODE || 'development',
    import.meta.env.VITE_POSTHOG_DEV_ENABLED
  )
  const postHogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        <ColorSchemeScript defaultColorScheme="auto" />
      </head>
      <body>
        {/* DeferredPostHogProvider loads PostHog SDK lazily after first paint.
            This improves initial load performance by ~150KB+ of JavaScript deferred.
            PostHog context is always available, but usePostHog() returns undefined until loaded. */}
        <DeferredPostHogProvider
          apiKey={shouldInitPostHog && postHogKey ? postHogKey : ''}
        >
          <MantineProvider
            theme={defaultTheme}
            defaultColorScheme="auto"
            cssVariablesResolver={cssVariablesResolver}
          >
            <ModalsProvider>
              {children}
            </ModalsProvider>
          </MantineProvider>
        </DeferredPostHogProvider>
        {/* Devtools only in development mode */}
        {import.meta.env.MODE === 'development' && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtools buttonPosition="top-left"/>
            <TanStackRouterDevtools position="top-left" />
          </React.Suspense>
        )}
        <Scripts />
      </body>
    </html>
  )
}